// Arcade menu navigation: arrows (or WASD) move a highlight across whichever
// overlay is on top, Enter/Space activates, Escape cancels. Selection is
// spatial — nearest button in the pressed direction by bounding rect — so
// dynamically built grids (keypad, perk cards, board tabs) need no wiring.
// The gamepad module drives the same nav* entry points with the d-pad.

type Dir = 'up' | 'down' | 'left' | 'right';

// Must match DOM order in index.html — the last visible overlay is on top.
const OVERLAY_IDS = [
  'menu', 'gameover', 'board', 'paused', 'audio-settings', 'badges', 'congregation', 'name-entry', 'perk', 'v4v',
];

const DIR_KEYS: Record<string, Dir> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
};
// WASD also navigates, except in name entry where letters must type.
const WASD_KEYS: Record<string, Dir> = {
  KeyW: 'up', KeyS: 'down', KeyA: 'left', KeyD: 'right',
};

interface NavContext {
  overlay: HTMLElement;
  items: HTMLElement[];
}

let selected: HTMLElement | null = null;

function context(): NavContext | null {
  let active: HTMLElement | null = null;
  for (const id of OVERLAY_IDS) {
    const el = document.getElementById(id);
    if (el && !el.hidden) active = el;
  }
  if (!active) return null;
  const items = [...active.querySelectorAll<HTMLElement>('button, a[href]')].filter((el) => {
    if (el.hidden || (el as HTMLButtonElement).disabled) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  });
  return items.length > 0 ? { overlay: active, items } : null;
}

function defaultPick(ctx: NavContext): HTMLElement {
  const { overlay, items } = ctx;
  // Honour system: never preselect I PAID on the v4v ask.
  if (overlay.id === 'v4v') {
    const thanks = document.getElementById('v4v-thanks');
    const id = thanks && !thanks.hidden ? 'v4v-start' : 'v4v-later';
    return document.getElementById(id) ?? items[0];
  }
  if (overlay.id === 'name-entry') {
    return overlay.querySelector<HTMLElement>('.kp-ok') ?? items[0];
  }
  if (overlay.id === 'audio-settings') {
    return document.getElementById('audio-master') ?? items[0];
  }
  const primary = overlay.querySelector<HTMLElement>('.btn-primary');
  return primary && items.includes(primary) ? primary : items[0];
}

function cancelTarget(overlay: HTMLElement): HTMLElement | null {
  switch (overlay.id) {
    case 'board': return document.getElementById('board-close');
    case 'badges': return document.getElementById('badges-close');
    case 'congregation': return document.getElementById('souls-close');
    case 'audio-settings': return document.getElementById('audio-close');
    case 'paused': return document.getElementById('resume');
    case 'gameover': return document.getElementById('to-menu');
    case 'name-entry': return overlay.querySelector('.kp-del');
    case 'v4v': {
      const thanks = document.getElementById('v4v-thanks');
      return document.getElementById(thanks && !thanks.hidden ? 'v4v-start' : 'v4v-later');
    }
    default: return null;
  }
}

function select(el: HTMLElement | null): void {
  if (selected === el) return;
  selected?.classList.remove('navsel');
  selected = el;
  if (el) {
    el.classList.add('navsel');
    // Pointer/touch users must always see the top of short panels. Only scroll
    // a selection into view once keyboard/gamepad focus is actually visible.
    if (document.body.dataset.kbnav === '1') el.scrollIntoView({ block: 'nearest' });
  }
}

function reseed(): void {
  const ctx = context();
  select(ctx ? defaultPick(ctx) : null);
}

function move(dir: Dir, ctx: NavContext): void {
  if (!selected || !ctx.items.includes(selected)) {
    select(defaultPick(ctx));
    return;
  }
  const cur = selected.getBoundingClientRect();
  const cx = cur.left + cur.width / 2;
  const cy = cur.top + cur.height / 2;
  const axis = (r: DOMRect): { fwd: number; ortho: number } => {
    const ex = r.left + r.width / 2 - cx;
    const ey = r.top + r.height / 2 - cy;
    const fwd = dir === 'left' ? -ex : dir === 'right' ? ex : dir === 'up' ? -ey : ey;
    const ortho = dir === 'left' || dir === 'right' ? Math.abs(ey) : Math.abs(ex);
    return { fwd, ortho };
  };
  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const el of ctx.items) {
    if (el === selected) continue;
    const { fwd, ortho } = axis(el.getBoundingClientRect());
    if (fwd < 1) continue;
    const score = fwd + ortho * 2.5;
    if (score < bestScore) { bestScore = score; best = el; }
  }
  if (!best) {
    // Nothing ahead: wrap to the farthest item behind (arcade list wrap).
    let far = -Infinity;
    for (const el of ctx.items) {
      if (el === selected) continue;
      const { fwd, ortho } = axis(el.getBoundingClientRect());
      const back = -fwd;
      if (back < 1) continue;
      const score = back - ortho * 2.5;
      if (score > far) { far = score; best = el; }
    }
  }
  if (best) select(best);
}

function activate(ctx: NavContext): void {
  if (!selected || !ctx.items.includes(selected)) select(defaultPick(ctx));
  selected?.click();
}

export function showFocus(): void {
  document.body.dataset.kbnav = '1';
  if (!selected) reseed();
  else selected.scrollIntoView({ block: 'nearest' });
}

function hideFocus(): void {
  delete document.body.dataset.kbnav;
}

export function uiActive(): boolean {
  return context() !== null;
}

export function navMove(dir: Dir): void {
  const ctx = context();
  if (!ctx) return;
  showFocus();
  move(dir, ctx);
}

export function navActivate(): void {
  const ctx = context();
  if (!ctx) return;
  showFocus();
  activate(ctx);
}

export function navCancel(): void {
  const ctx = context();
  if (!ctx) return;
  cancelTarget(ctx.overlay)?.click();
}

/** Start button: press the overlay's default action (PLAY, GO AGAIN, OK…). */
export function navStart(): void {
  const ctx = context();
  if (!ctx) return;
  showFocus();
  defaultPick(ctx).click();
}

export function initMenuNav(): void {
  window.addEventListener('keydown', (e) => {
    const ctx = context();
    if (!ctx) return;
    const nameEntry = ctx.overlay.id === 'name-entry';
    const dir = DIR_KEYS[e.code] ?? (nameEntry ? undefined : WASD_KEYS[e.code]);
    if (dir) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showFocus();
      move(dir, ctx);
      return;
    }
    if (e.code === 'Enter' || e.code === 'NumpadEnter' || (!nameEntry && e.code === 'Space')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!e.repeat) activate(ctx);
      return;
    }
    if ((e.code === 'Escape' || e.code === 'Backspace') && !nameEntry) {
      const target = cancelTarget(ctx.overlay);
      if (target) {
        e.preventDefault();
        e.stopImmediatePropagation();
        target.click();
      }
    }
  }, true);

  window.addEventListener('pointerdown', hideFocus, true);

  // Re-seed the highlight whenever an overlay opens, closes, or swaps panels.
  const observer = new MutationObserver(() => requestAnimationFrame(reseed));
  for (const id of [...OVERLAY_IDS, 'v4v-ask', 'v4v-thanks']) {
    const el = document.getElementById(id);
    if (el) observer.observe(el, { attributes: true, attributeFilter: ['hidden'] });
  }
  reseed();
}
